import { ModelEntityType } from '@upright/common/model/input/types';
import { SdgDrillDownTableRow } from '@upright/common/model/output/scores/sdg-alignment/api-types';
import {
  SdgReasoningDirectionApi,
  SdgTargetReasoningKey,
  SdgTargetReasoningResult,
} from '@upright/common/model/output/scores/sdg-alignment/sdg-target-reasoning-api-types';
import { SdgAlignmentValence, SdgTarget } from '@upright/common/model/output/scores/sdg-alignment/types';
import { uniqBy } from 'lodash';

export function valenceToReasoningDirection(valence: SdgAlignmentValence): SdgReasoningDirectionApi {
  return valence === SdgAlignmentValence.ALIGNED
    ? SdgReasoningDirectionApi.POSITIVE
    : SdgReasoningDirectionApi.NEGATIVE;
}

/**
 * Collects the unique (productCode, sdgTarget, direction) triples surfaced by a
 * set of SDG drilldown rows. Only product rows carry a product code, so
 * non-product constituents (groups/portfolios) are skipped.
 */
export function collectReasoningKeys(
  rows: SdgDrillDownTableRow[],
  valence: SdgAlignmentValence,
): SdgTargetReasoningKey[] {
  const direction = valenceToReasoningDirection(valence);
  const keys = rows.flatMap(row => {
    if (row.type !== ModelEntityType.PRODUCT) {
      return [];
    }
    return row.targets.flatMap(({ target }) => (target ? [{ productCode: row.id, sdgTarget: target, direction }] : []));
  });
  return uniqBy(keys, reasoningKeyString);
}

export function reasoningKeyString(key: SdgTargetReasoningKey): string {
  return buildReasoningKey(key.productCode, key.sdgTarget, key.direction);
}

export function buildReasoningKey(
  productCode: string,
  sdgTarget: SdgTarget,
  direction: SdgReasoningDirectionApi,
): string {
  return `${productCode}|${sdgTarget}|${direction}`;
}

export type ReasoningStatus = 'loading' | 'ready' | 'failed';

export interface ReasoningState {
  status: ReasoningStatus;
  /** The generated reasoning sentence — present only when `status` is 'ready'. */
  reasoning?: string;
}

export type ReasoningStateByKey = Map<string, ReasoningState>;

/**
 * Builds the per-key tooltip render state for the SDG drilldown:
 *
 *   - a key present in `results` is `ready` (show the generated reasoning);
 *   - otherwise, while generation is still in progress, it is `loading`
 *     (show "Reasoning loading...");
 *   - once generation has stopped (`generationStopped`: the batch errored,
 *     the polling window elapsed, or the batch completed with the key still
 *     unresolved) it is `failed` (show nothing).
 */
export function buildReasoningStateLookup(
  requestedKeys: SdgTargetReasoningKey[],
  results: SdgTargetReasoningResult[],
  options: { generationStopped: boolean },
): ReasoningStateByKey {
  const reasoningByKey = new Map(results.map(result => [reasoningKeyString(result), result.reasoning]));
  const stateByKey: ReasoningStateByKey = new Map();
  for (const key of requestedKeys) {
    const keyString = reasoningKeyString(key);
    const reasoning = reasoningByKey.get(keyString);
    if (reasoning !== undefined) {
      stateByKey.set(keyString, { status: 'ready', reasoning });
    } else {
      stateByKey.set(keyString, { status: options.generationStopped ? 'failed' : 'loading' });
    }
  }
  return stateByKey;
}
